import type { PageNumber, PDFSplitResult } from "@pdfking/renderer-api"
import type { PdfEngine } from "../types/engine/PdfEngine"
import type { PdfEngineOptions } from "../types/engine/PdfEngineOptions"
import type { SinglePagePdf } from "../types/files/cached/SinglePagePdf"
import type { VirtualFile } from "../types/files/VirtualFile"
import type { TaskResult } from "../types/tasks/input/TaskResult"
import type { TaskSplitOptions } from "../types/tasks/options/TaskSplitOptions"
import type { TrackingCallback } from "../types/tasks/tracking/TrackingCallback"
import { Min } from "@pdfking/math"
import { PDFConvert, PDFSplit } from "@pdfking/renderer-default"
import { Peek, Range } from "@pdfking/tfunc"
import { GenerateFile } from "../files/GenerateFile"
import { RequestBlob } from "../files/RequestBlob"
import { ConvertPixels } from "../geometry/ConvertPixels"
import { FileContentType } from "../methods/FileContentType"
import { ContentType } from "../types/files/ContentType"
import { VirtualFileType } from "../types/files/VirtualFileType"
import { TaskName } from "../types/tasks/TaskName"
import { TrackedTaskName } from "../types/tasks/tracking/TrackedTaskName"

type SplitPagesResult = {
  page_count: PageNumber
} & PDFSplitResult

const SplitPages = async function* (
  engine: PdfEngine,
  options: PdfEngineOptions,
  file: VirtualFile,
  start_page: number,
  end_page: number,
  emit: TrackingCallback
): AsyncGenerator<SplitPagesResult> {
  const { size, stream } = await RequestBlob(engine, file, emit)
  const type = FileContentType(file)

  switch (type) {
    case ContentType.PDF: {
      let page_count: PageNumber = 0 as PageNumber
      for await (const result of PDFSplit({ size, stream, type }, total => {
        page_count = total as PageNumber
        return Range(start_page, Min(end_page, total))
      })) {
        yield { ...result, page_count }
      }
      break
    }

    case ContentType.PNG:
    case ContentType.JPEG: {
      const result = await PDFConvert(
        { size, stream, type },
        options.image_resolution
      )
      yield { ...result, page_count: 1 as PageNumber }
      break
    }
  }
}

export async function* TaskSplit(
  task: TaskSplitOptions,
  emit: TrackingCallback,
  engine: PdfEngine
): AsyncGenerator<TaskResult> {
  const { stream, value: first } = await Peek(
    SplitPages(
      engine,
      task.options,
      task.source,
      task.start_page,
      task.end_page,
      emit
    )
  )

  if (!first) return

  const total_pages = first.page_count
  const end_page = Min(task.end_page, total_pages)
  const total = end_page - task.start_page + 1

  void emit({
    done: 0,
    name: TrackedTaskName.SPLIT_PDF,
    total,
  })

  let page_number: PageNumber = (task.start_page - 1) as PageNumber

  for await (const { blob, media_box, page_count, trim_box, size } of stream) {
    page_number = (page_number + 1) as PageNumber

    const content = await GenerateFile(engine, [blob], ContentType.PDF, emit)

    yield {
      content: {
        content,
        context: undefined,
        enum: VirtualFileType.SINGLE_PAGE_PDF,
        file_meta: {},
        media_box: ConvertPixels(task.options, media_box),
        page: undefined,
        page_count,
        page_number,
        size: ConvertPixels(task.options, size),
        source: task.source,
        trim_box: ConvertPixels(task.options, trim_box),
      } as SinglePagePdf,
      enum: TaskName.CONTENT_FILE,
      name: "page.pdf",
    } as TaskResult

    void emit({
      done: page_number - task.start_page + 1,
      name: TrackedTaskName.SPLIT_PDF,
      total,
    })
  }
}
